import { Link } from "react-router-dom";
import { Compass, Sparkles, Share2 } from "lucide-react";
import { BRAND } from "@/lib/brand";
import { useProgrammes, useStories } from "@/hooks/useCatalogue";
import PageIntro from "@/components/PageIntro";
import Reveal from "@/components/Reveal";

const dise = [
  { icon: Compass, title: "Discover", text: "Explore products and the thinking behind them." },
  { icon: Sparkles, title: "Experience", text: "Apply for an available programme, subject to eligibility." },
  { icon: Share2, title: "Share", text: "Describe how a product fits into your everyday skincare." },
];

export default function Community() {
  const { data: programmes = [] } = useProgrammes();
  const { data: stories = [] } = useStories();

  return (
    <div>
      <PageIntro
        eyebrow={BRAND.descriptor}
        title="Rituals With You"
        description="A community built on an honest, two-way relationship. Discover our products, experience them through programmes, and share your ritual. We welcome positive and critical experiences alike."
      >
        <Link
          to="/join"
          className="mt-8 inline-flex btn btn-primary"
        >
          {BRAND.joinCta}
        </Link>
      </PageIntro>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid md:grid-cols-3 gap-6">
          {dise.map(({ icon: Icon, title, text }, i) => (
            <Reveal key={title} delay={i * 0.12} className="border border-forest/15 bg-cream p-8">
              <div className="w-12 h-12 rounded-full bg-forest/8 flex items-center justify-center text-forest">
                <Icon size={22} />
              </div>
              <h3 className="mt-5 font-serif text-2xl text-forest">{title}</h3>
              <p className="mt-2 text-sm text-charcoal/70">{text}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <Reveal as="section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h2 className="font-serif text-3xl text-forest mb-8">Open programmes</h2>
        {programmes.length === 0 ? (
          <div className="border border-dashed border-forest/25 bg-cream/60 p-12 text-center">
            <p className="font-serif text-2xl text-forest">Nothing open right now</p>
            <p className="mt-3 text-charcoal/70 max-w-md mx-auto">
              Join the community and we'll invite you when a programme that fits opens.
            </p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-3">
            {programmes.map((pr) => (
              <Link key={pr.slug} to={`/programmes/${pr.slug}`} className="border border-forest/15 bg-cream p-6 hover:border-forest/40 transition-colors">
                <span className="text-[11px] uppercase tracking-[0.24em] text-champagne">{pr.status}</span>
                <h3 className="mt-2 font-serif text-2xl text-forest">{pr.title}</h3>
                <p className="mt-2 text-sm text-charcoal/70">{pr.summary}</p>
              </Link>
            ))}
          </div>
        )}
      </Reveal>

      {stories.length > 0 && (
        <Reveal as="section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h2 className="font-serif text-3xl text-forest mb-8">Member stories</h2>
          <div className="grid gap-6 md:grid-cols-3">
            {stories.map((s) => (
              <div key={s.story_id} className="border border-forest/15 bg-cream p-6">
                <p className="text-sm text-charcoal/80 leading-relaxed">"{s.body}"</p>
                <p className="mt-4 text-[10px] uppercase tracking-[0.24em] text-moss">{s.display_name}</p>
              </div>
            ))}
          </div>
        </Reveal>
      )}
    </div>
  );
}
